import { assertUnreachable } from '@sgme/fp';
import { useContext } from 'react';

import {
  TargetAccumulatorContext,
  useTargetAccumulatorSelector,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { Awaiting } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/Awaiting';
import { NotPriceable } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/NotPriceable.tsx';
import { Pricing } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/Pricing/index.tsx';
import { StreamingError } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/StreamingError.tsx';
import { isNotDefined } from '@/components/sg-design-system/utils/index.ts';
import { getTargetAccumulatorQuote, selectTargetAccumulatorRfsStatus } from '@/store/targetAccumulator/selectors.ts';
import type { TargetAccumulatorRfsStatus } from '@/store/targetAccumulator/state.ts';

function getPriceSummaryContent(tileId: string, rfsStatus: TargetAccumulatorRfsStatus | undefined, hasQuote: boolean) {
  if (isNotDefined(rfsStatus)) {
    return null;
  }

  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceable />;
    case 'PRICEABLE': {
      if (hasQuote) {
        return <Pricing tileId={tileId} />;
      }
      return <NotPriceable />;
    }
    case 'AWAITING_INDICATIVE':
      return <Awaiting forceTradeable={false} />;
    case 'AWAITING_EXECUTABLE':
      return <Awaiting forceTradeable={true} />;
    case 'STREAMING_INDICATIVE':
      return <Pricing tileId={tileId} />;
    case 'STREAMING_EXECUTABLE':
      return <Pricing tileId={tileId} isExecutable />;
    case 'ERROR':
    case 'BYPASSABLE_ERROR':
      return <StreamingError />;
    default:
      assertUnreachable(rfsStatus, `Unknown price status: ${rfsStatus}`);
  }
}

export function TargetAccumulatorPriceSummary() {
  const { tileId } = useContext(TargetAccumulatorContext);
  const { rfsStatus, hasQuote } = useTargetAccumulatorSelector((targetAccumulator) => ({
    rfsStatus: selectTargetAccumulatorRfsStatus(targetAccumulator),
    hasQuote: getTargetAccumulatorQuote(targetAccumulator) !== undefined,
  }));

  return (
    <div style={{ height: '100px', maxWidth: '323px' }} data-e2e="price-summary">
      {getPriceSummaryContent(tileId, rfsStatus, hasQuote)}
    </div>
  );
}
